import type { CityTransit } from '@routellers/types/dist/openapi/city-player/city-transit';
import {
    FieldCity,
    FieldTarget,
    FieldDetail,
    FieldCommand,
    FieldEvent,
} from '@routellers/types/dist/openapi/city-player/city-field';
import {
    CityExperienceReviewRO,
    CityExperienceReviewsFeedRO,
} from '@routellers/types/dist/city-experience-review/city-experience-review.ro';
import { CreateExperienceReviewDto } from '@routellers/types/dist/city-experience-review/create-experience-review.dto';
import {
    CityPlayerGygActivitiesRO,
    GygActivityRO,
} from '@routellers/types/dist/gyg-activity/gyg-activity.ro';
import { CityPlayerEstablishmentsRO } from '@routellers/types/dist/openapi/city-player/city-establishments.ro';
import {
    CityPlayerAttractionsMapRO,
    CityPlayerPeopleRO,
    CityPlayerPersonDetailRO,
} from '@routellers/types/dist/openapi/city-player/city-explore.ro';
import {
    CityGuideDistrictDetailRO,
    CityGuideRO,
} from '@routellers/types/dist/openapi/city-player/city-guide.ro';
import {
    CityPlayerCatalogRO,
    CityPlayerCityPageRO,
    CityPlayerGameRO,
    CityPlayerLoginRO,
    CityPlayerQuestPreviewRO,
    CityPlayerRequestCodeRO,
    CityPlayerRO,
    CityPlayerVerifyCodeRO,
} from '@routellers/types/dist/openapi/city-player/city-player.ro';
import { CityPlayerAppleLoginDto } from '@routellers/types/dist/openapi/city-player/city-player-apple-login.dto';
import { CityPlayerLoginDto } from '@routellers/types/dist/openapi/city-player/city-player-login.dto';
import { CityPlayerRequestCodeDto } from '@routellers/types/dist/openapi/city-player/city-player-request-code.dto';
import { CityPlayerVerifyCodeDto } from '@routellers/types/dist/openapi/city-player/city-player-verify-code.dto';
import {
    CityPlayerReadyRouteDetailRO,
    CityPlayerReadyRoutesRO,
} from '@routellers/types/dist/openapi/city-player/city-ready-route.ro';
import {
    CityStaffNoteRO,
    CityStaffPhotosRO,
    CityStaffRouteOverlayRO,
    CityStaffWaypointRO,
} from '@routellers/types/dist/openapi/city-player/city-staff';
import type {
    CityPlayerTogetherPublicRO,
    CityPlayerTogetherRoomRO,
    CityPlayerTogetherStateDto,
} from '@routellers/types/dist/openapi/city-player/city-together';
import { CityPlayerFreeRouteRO } from '@routellers/types/dist/openapi/city-player/free-route';

import { ApiMethodDeclaration } from '..';

/**
 * API потребительского игрока (Routellers). Один app — два бека: запросы
 * идут через failover-обёртку в приложении, которая при 404/401 ретраит
 * второй домен и закрепляет рабочий.
 */

const ping: ApiMethodDeclaration<void, { ok: boolean; time: string }> = () => ({
    url: '/openapi/city-player/ping',
    method: 'GET',
});

const login: ApiMethodDeclaration<{ data: CityPlayerLoginDto }, CityPlayerLoginRO> = ({
    data,
}) => ({
    url: '/openapi/city-player/login',
    method: 'POST',
    data,
});

const requestCode: ApiMethodDeclaration<
    { data: CityPlayerRequestCodeDto },
    CityPlayerRequestCodeRO
> = ({ data }) => ({
    url: '/openapi/city-player/request-code',
    method: 'POST',
    data,
});

const verifyCode: ApiMethodDeclaration<
    { data: CityPlayerVerifyCodeDto },
    CityPlayerVerifyCodeRO
> = ({ data }) => ({
    url: '/openapi/city-player/verify-code',
    method: 'POST',
    data,
});

/** Обмен одноразовой ссылки из письма о покупке на постоянный JWT. */
const magicLogin: ApiMethodDeclaration<
    { data: { magicToken: string } },
    CityPlayerVerifyCodeRO
> = ({ data }) => ({
    url: '/openapi/city-player/magic-login',
    method: 'POST',
    data,
});

/**
 * Слияние аккаунтов: подтверждение кодом владения email с веб-покупками.
 * В ответе JWT владельца email — клиент обязан заменить сохранённый токен.
 */
const linkEmail: ApiMethodDeclaration<
    { data: CityPlayerVerifyCodeDto },
    CityPlayerVerifyCodeRO
> = ({ data }) => ({
    url: '/openapi/city-player/link-email',
    method: 'POST',
    data,
});

const appleLogin: ApiMethodDeclaration<
    { data: CityPlayerAppleLoginDto },
    CityPlayerVerifyCodeRO
> = ({ data }) => ({
    url: '/openapi/city-player/apple',
    method: 'POST',
    data,
});

const me: ApiMethodDeclaration<void, CityPlayerRO> = () => ({
    url: '/openapi/city-player/me',
    method: 'GET',
});

const games: ApiMethodDeclaration<void, CityPlayerGameRO[]> = () => ({
    url: '/openapi/city-player/games',
    method: 'GET',
});

const catalog: ApiMethodDeclaration<
    { lat?: number; lng?: number; language?: string } | void,
    CityPlayerCatalogRO
> = (params) => ({
    url: '/openapi/city-player/catalog',
    method: 'GET',
    params: params || undefined,
});

const catalogUpcoming: ApiMethodDeclaration<{ language?: string } | void, CityPlayerCatalogRO> = (
    params,
) => ({
    url: '/openapi/city-player/catalog/upcoming',
    method: 'GET',
    params: params || undefined,
});

const cityCollections: ApiMethodDeclaration<
    { cityId: number; language?: string },
    import('@routellers/types/dist/city-collection/city-collection.ro').PublicCityCollectionsRO
> = ({ cityId, language }) => ({
    url: `/openapi/city-player/cities/${cityId}/collections`,
    method: 'GET',
    params: { language },
});
const cityCollection: ApiMethodDeclaration<
    { cityId: number; slug: string; language?: string },
    import('@routellers/types/dist/city-collection/city-collection.ro').PublicCityCollectionRO
> = ({ cityId, slug, language }) => ({
    url: `/openapi/city-player/cities/${cityId}/collections/by-slug/${encodeURIComponent(slug)}`,
    method: 'GET',
    params: { language },
});

const cityBySlug: ApiMethodDeclaration<
    { slug: string; language?: string },
    CityPlayerCityPageRO | null
> = ({ slug, language }) => ({
    url: `/openapi/city-player/cities/by-slug/${slug}`,
    method: 'GET',
    params: { language },
});

const cityPeople: ApiMethodDeclaration<
    { cityId: number; language?: string },
    CityPlayerPeopleRO
> = ({ cityId, language }) => ({
    url: `/openapi/city-player/cities/${cityId}/people`,
    method: 'GET',
    params: { language },
});

const cityAttractions: ApiMethodDeclaration<
    { cityId: number; language?: string },
    CityPlayerAttractionsMapRO
> = ({ cityId, language }) => ({
    url: `/openapi/city-player/cities/${cityId}/attractions`,
    method: 'GET',
    params: { language },
});

const cityEstablishments: ApiMethodDeclaration<
    { cityId: number; language?: string },
    CityPlayerEstablishmentsRO
> = ({ cityId, language }) => ({
    url: `/openapi/city-player/cities/${cityId}/establishments`,
    method: 'GET',
    params: { language },
});

const cityFood: ApiMethodDeclaration<
    { cityId: number; slug: string; language?: string },
    import('@routellers/types/dist/openapi/city-player/city-guide.ro').CityGuideFoodDetailRO
> = ({ cityId, slug, language }) => ({
    url: `/openapi/city-player/cities/${cityId}/foods/by-slug/${encodeURIComponent(slug)}`,
    method: 'GET',
    params: { language },
});

const cityTransit: ApiMethodDeclaration<{ cityId: number }, CityTransit> = ({ cityId }) => ({
    url: `/openapi/city-player/cities/${cityId}/transit`,
    method: 'GET',
});

const cityGuide: ApiMethodDeclaration<{ cityId: number; language?: string }, CityGuideRO> = ({
    cityId,
    language,
}) => ({
    url: `/openapi/city-player/cities/${cityId}/guide`,
    method: 'GET',
    params: { language },
});

const cityDistrict: ApiMethodDeclaration<
    { cityId: number; slug: string; language?: string },
    CityGuideDistrictDetailRO
> = ({ cityId, slug, language }) => ({
    url: `/openapi/city-player/cities/${cityId}/districts/by-slug/${encodeURIComponent(slug)}`,
    method: 'GET',
    params: { language },
});

const personDetail: ApiMethodDeclaration<
    { personId: number; cityId?: number; language?: string },
    CityPlayerPersonDetailRO
> = ({ personId, cityId, language }) => ({
    url: `/openapi/city-player/people/${personId}`,
    method: 'GET',
    params: { cityId, language },
});

const questPreview: ApiMethodDeclaration<
    number | { id: number; language?: string },
    CityPlayerQuestPreviewRO
> = (params) => {
    const id = typeof params === 'number' ? params : params.id;
    const language = typeof params === 'number' ? undefined : params.language;

    return {
        url: `/openapi/city-player/quests/${id}`,
        method: 'GET',
        params: { language },
    };
};

const freeRoute: ApiMethodDeclaration<
    {
        cityId: number;
        personId?: number;
        maxStops?: number;
        maxDurationMin?: number;
        language?: string;
    },
    CityPlayerFreeRouteRO
> = (params) => ({
    url: '/openapi/city-player/free-route',
    method: 'GET',
    params,
});

const cityReadyRoutes: ApiMethodDeclaration<
    { cityId: number; language?: string },
    CityPlayerReadyRoutesRO
> = ({ cityId, language }) => ({
    url: `/openapi/city-player/cities/${cityId}/ready-routes`,
    method: 'GET',
    params: { language },
});

const cityActivities: ApiMethodDeclaration<{ cityId: number }, CityPlayerGygActivitiesRO> = ({
    cityId,
}) => ({
    url: `/openapi/city-player/cities/${cityId}/activities`,
    method: 'GET',
});

const cityActivityBySlug: ApiMethodDeclaration<{ cityId: number; slug: string }, GygActivityRO> = ({
    cityId,
    slug,
}) => ({
    url: `/openapi/city-player/cities/${cityId}/activities/by-slug/${slug}`,
    method: 'GET',
});

const cityActivityById: ApiMethodDeclaration<number, GygActivityRO> = (id) => ({
    url: `/openapi/city-player/activities/${id}`,
    method: 'GET',
});

const readyRouteDetail: ApiMethodDeclaration<
    { routeId: number; language?: string },
    CityPlayerReadyRouteDetailRO
> = ({ routeId, language }) => ({
    url: `/openapi/city-player/ready-routes/${routeId}`,
    method: 'GET',
    params: { language },
});

const entitlements: ApiMethodDeclaration<void, unknown[]> = () => ({
    url: '/openapi/city-player/entitlements',
    method: 'GET',
});

const devGrantEntitlement: ApiMethodDeclaration<
    { scope: 'route' | 'city' | 'quest' | 'pass'; scopeId?: number | null; secret?: string },
    { accessToken?: string | null }
> = (data) => ({
    url: '/openapi/city-player/entitlements/dev-grant',
    method: 'POST',
    data,
});

const devRevokeEntitlement: ApiMethodDeclaration<
    { entitlementId: number; secret?: string },
    unknown
> = (data) => ({
    url: '/openapi/city-player/entitlements/dev-revoke',
    method: 'POST',
    data,
});

const iapGrantEntitlement: ApiMethodDeclaration<
    {
        scope: 'route' | 'city' | 'quest' | 'pass';
        scopeId?: number | null;
        storeTransactionId: string;
        productId?: string;
    },
    { accessToken?: string | null }
> = (data) => ({
    url: '/openapi/city-player/entitlements/iap-grant',
    method: 'POST',
    data,
});

/**
 * Привязка партнёрского клейма к игроку: диплинк с claimToken или ручной
 * ввод кода. Эндпоинт живёт в city-payment, но авторизуется как city-player.
 */
const cloudpaymentsCheckout: ApiMethodDeclaration<
    {
        data: {
            scope: 'route' | 'city' | 'quest' | 'pass';
            scopeId?: number | null;
            locale?: string;
            returnUrl?: string;
        };
    },
    { checkoutUrl: string; invoiceId: string; amountRub: number; currency: string }
> = ({ data }) => ({
    url: '/openapi/city-payment/cloudpayments-app-checkout',
    method: 'POST',
    data,
});

const linkPromoClaim: ApiMethodDeclaration<
    { claimToken?: string; promocode?: string },
    {
        claimToken: string;
        partnerName: string;
        citySlug: string | null;
        percentOff: number;
        expiresAt: string;
    } | null
> = (data) => ({
    url: '/openapi/city-payment/claim-link',
    method: 'POST',
    data,
});

const cityReviews: ApiMethodDeclaration<
    { cityId: number; language?: string; limit?: number },
    CityExperienceReviewsFeedRO
> = ({ cityId, language, limit }) => ({
    url: `/openapi/city-player/cities/${cityId}/reviews`,
    method: 'GET',
    params: { language, limit },
});

const routeReviews: ApiMethodDeclaration<
    { routeId: number; language?: string; limit?: number },
    CityExperienceReviewsFeedRO
> = ({ routeId, language, limit }) => ({
    url: `/openapi/city-player/ready-routes/${routeId}/reviews`,
    method: 'GET',
    params: { language, limit },
});

const questReviews: ApiMethodDeclaration<
    { questId: number; language?: string; limit?: number },
    CityExperienceReviewsFeedRO
> = ({ questId, language, limit }) => ({
    url: `/openapi/city-player/quests/${questId}/reviews`,
    method: 'GET',
    params: { language, limit },
});

const fieldCity: ApiMethodDeclaration<{ cityId: number; language: string }, FieldCity> = ({
    cityId,
    language,
}) => ({ url: `/openapi/city-player/field/cities/${cityId}`, method: 'GET', params: { language } });
const fieldDetail: ApiMethodDeclaration<FieldTarget & { language: string }, FieldDetail> = ({
    kind,
    id,
    language,
}) => ({
    url: `/openapi/city-player/field/objects/${kind}/${id}`,
    method: 'GET',
    params: { language },
});
const fieldJournal: ApiMethodDeclaration<{ cityId: number }, FieldEvent[]> = ({ cityId }) => ({
    url: `/openapi/city-player/field/journal/${cityId}`,
    method: 'GET',
});
const fieldCommand: ApiMethodDeclaration<{ data: FieldCommand }, FieldEvent> = ({ data }) => ({
    url: '/openapi/city-player/field/commands',
    method: 'POST',
    data,
});

const staffRoute: ApiMethodDeclaration<{ routeId: number }, CityStaffRouteOverlayRO> = ({
    routeId,
}) => ({
    url: `/openapi/city-player/staff/routes/${routeId}`,
    method: 'GET',
});

const addStaffWaypoint: ApiMethodDeclaration<
    { data: { routeId: number; afterStopId: number; lat: number; lng: number } },
    CityStaffWaypointRO
> = ({ data }) => ({
    url: '/openapi/city-player/staff/waypoints',
    method: 'POST',
    data,
});

const moveStaffWaypoint: ApiMethodDeclaration<
    { stopId: number; data: { lat: number; lng: number } },
    CityStaffWaypointRO
> = ({ stopId, data }) => ({
    url: `/openapi/city-player/staff/waypoints/${stopId}`,
    method: 'PATCH',
    data,
});

const deleteStaffWaypoint: ApiMethodDeclaration<{ stopId: number }, { ok: boolean }> = ({
    stopId,
}) => ({
    url: `/openapi/city-player/staff/waypoints/${stopId}`,
    method: 'DELETE',
});

const staffNotes: ApiMethodDeclaration<
    { routeStopId?: number; attractionId?: number },
    CityStaffNoteRO[]
> = ({ routeStopId, attractionId }) => ({
    url: '/openapi/city-player/staff/notes',
    method: 'GET',
    params: { routeStopId, attractionId },
});

const addStaffNote: ApiMethodDeclaration<
    { data: { body: string; routeStopId?: number | null; attractionId?: number | null } },
    CityStaffNoteRO
> = ({ data }) => ({
    url: '/openapi/city-player/staff/notes',
    method: 'POST',
    data,
});

const staffPhotos: ApiMethodDeclaration<
    { attractionId?: number; establishmentId?: number },
    CityStaffPhotosRO
> = ({ attractionId, establishmentId }) => ({
    url: '/openapi/city-player/staff/photos',
    method: 'GET',
    params: { attractionId, establishmentId },
});

const addStaffPhoto: ApiMethodDeclaration<
    {
        data: {
            attractionId?: number;
            establishmentId?: number;
            imageDataUrl: string;
        };
    },
    CityStaffPhotosRO
> = ({ data }) => ({
    url: '/openapi/city-player/staff/photos',
    method: 'POST',
    data,
    timeout: 60000,
});

const deleteStaffPhoto: ApiMethodDeclaration<
    { attractionId?: number; establishmentId?: number; index: number },
    CityStaffPhotosRO
> = ({ attractionId, establishmentId, index }) => ({
    url: '/openapi/city-player/staff/photos',
    method: 'DELETE',
    params: { attractionId, establishmentId, index },
});

const setStaffPhotoCover: ApiMethodDeclaration<
    { data: { attractionId?: number; establishmentId?: number; index: number } },
    CityStaffPhotosRO
> = ({ data }) => ({
    url: '/openapi/city-player/staff/photos/cover',
    method: 'POST',
    data,
});

const togetherOpen: ApiMethodDeclaration<
    { routeId: number; language?: string },
    CityPlayerTogetherRoomRO
> = ({ routeId, language }) => ({
    url: `/openapi/city-player/ready-routes/${routeId}/together`,
    method: 'POST',
    params: { language },
});

const togetherState: ApiMethodDeclaration<
    { code: string; data: CityPlayerTogetherStateDto },
    { ok: true }
> = ({ code, data }) => ({
    url: `/openapi/city-player/together/${code}/state`,
    method: 'POST',
    data,
});

const togetherClose: ApiMethodDeclaration<{ code: string }, { ok: true }> = ({ code }) => ({
    url: `/openapi/city-player/together/${code}`,
    method: 'DELETE',
});

const togetherPublic: ApiMethodDeclaration<
    { code: string; followerId?: string },
    CityPlayerTogetherPublicRO
> = ({ code, followerId }) => ({
    url: `/openapi/city-player/together/${code}`,
    method: 'GET',
    params: followerId ? { f: followerId } : {},
});

const submitReview: ApiMethodDeclaration<
    { data: CreateExperienceReviewDto },
    CityExperienceReviewRO
> = ({ data }) => ({
    url: '/openapi/city-player/reviews',
    method: 'POST',
    data,
});

export const cityPlayerApiDeclaration = {
    ping,
    login,
    magicLogin,
    linkEmail,
    requestCode,
    verifyCode,
    appleLogin,
    me,
    games,
    catalog,
    catalogUpcoming,
    cityBySlug,
    cityCollections,
    cityCollection,
    cityPeople,
    cityAttractions,
    cityEstablishments,
    cityGuide,
    cityTransit,
    cityFood,
    cityDistrict,
    personDetail,
    questPreview,
    freeRoute,
    cityReadyRoutes,
    cityActivities,
    cityActivityBySlug,
    cityActivityById,
    readyRouteDetail,
    cityReviews,
    routeReviews,
    questReviews,
    fieldCity,
    fieldDetail,
    fieldJournal,
    fieldCommand,
    staffRoute,
    addStaffWaypoint,
    moveStaffWaypoint,
    deleteStaffWaypoint,
    staffNotes,
    addStaffNote,
    staffPhotos,
    addStaffPhoto,
    deleteStaffPhoto,
    setStaffPhotoCover,
    togetherOpen,
    togetherState,
    togetherClose,
    togetherPublic,
    submitReview,
    entitlements,
    devGrantEntitlement,
    devRevokeEntitlement,
    iapGrantEntitlement,
    cloudpaymentsCheckout,
    linkPromoClaim,
};
